import { Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import { ComponentErrorBoundary } from "core/components/ComponentErrorBoundary";
import { ComponentSwitcher } from "core/components/ComponentSwitcher";
import { ErrorComponent } from "core/components/ErrorComponent";
import {
  GridAreaContainer,
  type GridAreaContainerProps,
} from "core/components/GridAreaContainer";
import { StyledTabs } from "core/components/StyledTabs";
import { useBrandCountryCode } from "core/hooks/useBrandCountryCode";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import type { WidgetTabValues } from "features/DMS/entities";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DMSWidgetList } from "./DMSWidgetList";

const GRID_AREA = "dms";

function Wrapper({
  children,
  containerProps,
}: {
  children: React.ReactNode;
  containerProps?: GridAreaContainerProps;
}) {
  const { t } = useTranslation("DocumentManagementSystem", {
    keyPrefix: "Next:DocumentManagementSystem:DMSWidget",
  });
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: GRID_AREA } }}>
      <GridAreaContainer
        {...containerProps}
        gridArea={GRID_AREA}
        title={t("gridAreaContainerTitle")}
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function DMSWidget({
  containerProps,
}: {
  containerProps?: GridAreaContainerProps;
}) {
  const { locale = "en" } = useRouter();
  const { t } = useTranslation("DocumentManagementSystem", {
    keyPrefix: "Next:DocumentManagementSystem:DMSWidget",
  });
  const [tabsValue, setTabsValue] = useState<WidgetTabValues>("publishTime");
  const brandCountryCode = useBrandCountryCode();
  const { isMobile } = useBreakpoints();
  return (
    <Wrapper containerProps={containerProps}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: isMobile ? "700px" : "570px",
          py: isMobile ? 2 : 4,
          px: isMobile ? 1 : 4,
        }}
      >
        <StyledTabs
          value={tabsValue}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab
            value="publishTime"
            label={t("tabLastPublishTime")}
            onClick={() => setTabsValue("publishTime")}
          />
          <Tab
            value="lastModificationTime"
            label={t("tabLastModificationTime")}
            onClick={() => setTabsValue("lastModificationTime")}
          />
        </StyledTabs>
        <Divider />
        <Divider />
        <Box minHeight="375px" overflow="auto">
          <ComponentSwitcher
            switchPosition={tabsValue === "publishTime"}
            ComponentOne={
              <WidgetContent
                tab="publishTime"
                locale={locale}
                brandCountryCode={brandCountryCode}
              />
            }
            ComponentTwo={
              <WidgetContent
                tab="lastModificationTime"
                locale={locale}
                brandCountryCode={brandCountryCode}
              />
            }
          />
        </Box>
      </Paper>
    </Wrapper>
  );
}
const MAX_ITEMS = 8;

function WidgetContent({
  tab,
  locale,
}: {
  tab: WidgetTabValues;
  locale: string;
  brandCountryCode: string;
}) {
  const { data, isLoading, isError } = useCentrixApi({
    path: "/api/app/doc-node/document-summary-with-parents-list",
    parameters: {
      query: {
        Locale: locale,
        Sorting: `${tab} desc`,
        Type: "Document",
        SkipCount: 0,
        MaxResultCount: MAX_ITEMS,
      },
    },
  });
  if (isError) {
    return <ErrorComponent apiError={isError} />;
  }
  if (isLoading) {
    return <Skeleton sx={{ mt: 4 }} variant="rounded" height="375px" />;
  }
  return (
    <DMSWidgetList
      isLoading={isLoading}
      documents={data?.items ?? []}
      selectedTab={tab}
    />
  );
}
